import { Tooltip } from "react-tooltip";
import { useNavigate } from "react-router";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { CheckCircleIcon, NoSymbolIcon, PencilIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../app/hooks";
import { UserRole } from "../../app/userSlice";
import { ClassLinkOrCleverIcon } from "../../components/ClassLinkOrCleverIcon";
import { SchoolListViewModel } from "./schoolsApiSlice";

interface SchoolListTableProps {
	schools: SchoolListViewModel[];
}

export default function SchoolListTable(props: SchoolListTableProps) {
	const { role } = useAppSelector((state) => state.user);
	const { config } = useAppSelector((state) => state.site);

	const navigate = useNavigate();

	const hasClassLinkOrCleverSchool = props.schools.some(
		(s) => s.isClassLinkSchool || s.isCleverSchool
	);

	const activeBodyTemplate = (row: SchoolListViewModel) => {
		const tooltipContent = `${row.hasExpired ? "Expired" : "Expires"}: ${row.expirationDate}`;

		return (
			<span className="flex justify-center">
				{row.active ? (
					<CheckCircleIcon
						className="size-6 text-green-500"
						data-tooltip-id={`enabled-${row.dT_RowId}-tooltip`}
					/>
				) : (
					<NoSymbolIcon
						className="size-6 text-red-500"
						data-tooltip-id={`enabled-${row.dT_RowId}-tooltip`}
					/>
				)}
				<Tooltip
					id={`enabled-${row.dT_RowId}-tooltip`}
					place="top-start"
					content={tooltipContent}
				/>
			</span>
		);
	};

	const cleverOrClassLinkBodyTemplate = (row: SchoolListViewModel) => {
		return (
			<ClassLinkOrCleverIcon
				isClassLinkEntity={row.isClassLinkSchool}
				isCleverEntity={row.isCleverSchool}
			/>
		);
	};

	const contactBodyTemplate = (row: SchoolListViewModel) => {
		return (
			<span>
				<span>{row.contact}</span>
				<div className="mt-1 text-gray-500 text-sm">{row.phone}</div>
			</span>
		);
	};

	const editBodyTemplate = (row: SchoolListViewModel) => {
		return (
			<button
				id={`edit-${row.dT_RowId}-button`}
				className="mr-2 cursor-pointer"
				onClick={() => navigate(`/schools/edit?schoolId=${row.dT_RowId}`)}>
				<PencilIcon className="size-5" />
			</button>
		);
	};

	return (
		<DataTable
			value={props.schools}
			dataKey="dT_RowId"
			emptyMessage="No schools found"
			stripedRows
			className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
			style={{ paddingTop: "16px" }}>
			<Column
				header="Active"
				body={activeBodyTemplate}
				headerClassName="!w-12"
				className="!w-12 !text-center"></Column>
			{!config?.isUtahComposeSite && hasClassLinkOrCleverSchool && (
				<Column
					body={cleverOrClassLinkBodyTemplate}
					headerClassName="!px-0 w-4"
					className="!px-0"></Column>
			)}
			<Column header="ID" field="id"></Column>
			<Column header="School Name" field="name"></Column>
			{role !== UserRole.DistrictAdmin && (
				<Column header="District Name" field="districtName"></Column>
			)}
			<Column header="Primary Contact" body={contactBodyTemplate}></Column>
			<Column header="Active Courses" field="numCourses"></Column>
			{!config?.unrestrictedEnrollments && <Column header="Seats" field="numSeats"></Column>}
			<Column body={editBodyTemplate} className="!pl-1 !pr-2"></Column>
		</DataTable>
	);
}
